import type { Path } from 'react-router';
import { useMatches } from 'react-router';
import { z } from 'zod';

const menuSectionSchema = z.object({
  header: z.string(),
  items: z.array(
    z.object({
      label: z.string(),
      to: z
        .object({
          pathname: z.string(),
          search: z.string(),
          hash: z.string(),
        })
        .optional(),
      onAction: z.custom<() => void>((val) => typeof val === 'function').optional(),
    }),
  ),
});

// A route builds its menu section from the route's loader data. Validating the handle only checks
// that the value is callable; the section the callback returns is checked each time it runs, and
// the loader data and environment key are handed to it untouched.
const schema = z.object({
  environmentSettingsMenuItems: z
    .function({
      input: [z.custom<{ data: unknown; environmentKey?: string }>()],
      output: menuSectionSchema,
    })
    .optional(),
});

export function useEnvironmentMenuItems(environmentKey?: string) {
  const matches = useMatches();
  const reversedMatches = [...matches].reverse();

  let section: { header: string; items: Array<{ label: string; to?: Path; onAction?: () => void }> } | undefined;
  for (const match of reversedMatches) {
    const parse = schema.safeParse(match.handle);
    if (parse.success) {
      if (parse.data.environmentSettingsMenuItems !== undefined) {
        const { header, items } = parse.data.environmentSettingsMenuItems({ data: match.loaderData, environmentKey });
        section = { header, items };
      }
    }
  }

  if (section === undefined) {
    return;
  }

  return section;
}
